/**
 * Operations Routes
 * Routes for attendance, hifdh progress, calendar, notifications, and timecard
 */

import { lazy } from "react";
import { RouteObject, useParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { LazyPage, RedirectKeepingState } from "../helpers";

/** An old daily-progress link opens the class's v2 sheet. */
function HifdhV1DayRedirect() {
  const { classId = "" } = useParams<{ classId: string }>();
  return <RedirectKeepingState to={`/hifdh/v2/classes/${encodeURIComponent(classId)}/day`} />;
}

/** An old memorization link opens the student's Hifdh Progress. */
function HifdhV1StudentRedirect() {
  const { studentId = "" } = useParams<{ studentId: string }>();
  return <RedirectKeepingState to={`/hifdh/v2/students/${encodeURIComponent(studentId)}`} />;
}

// Attendance
const AttendanceDashboardPage = lazy(() => import("@/pages/attendance").then(m => ({ default: m.AttendanceDashboardPage })));
const TakeAttendancePage = lazy(() => import("@/pages/attendance").then(m => ({ default: m.TakeAttendancePage })));
const SessionsListPage = lazy(() => import("@/pages/attendance").then(m => ({ default: m.SessionsListPage })));
const ClassSchedulesPage = lazy(() => import("@/pages/attendance").then(m => ({ default: m.ClassSchedulesPage })));

// Hifdh Progress
const HifdhReportCardPage = lazy(() => import("@/pages/hifdh").then(m => ({ default: m.HifdhReportCardPage })));
// Hifdh Progress v2 (plans and daily ratings)
const HifdhHomePage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.HifdhHomePage })));
const HifdhClassDayPage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.ClassDayPage })));
const HifdhStudentPage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.StudentPage })));
const HifdhPlanPage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.PlanPage })));
const HifdhClassMonthPage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.ClassMonthPage })));
const HifdhCertificatePage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.StaffCertificatePage })));
const HifdhClassCertificatesPrintPage = lazy(() => import("@/pages/hifdh/v2").then(m => ({ default: m.ClassCertificatesPrintPage })));

// Calendar
const CalendarViewPage = lazy(() => import("@/pages/calendar").then(m => ({ default: m.CalendarViewPage })));
const CalendarEventsListPage = lazy(() => import("@/pages/calendar").then(m => ({ default: m.CalendarEventsListPage })));
const CalendarEventFormPage = lazy(() => import("@/pages/calendar").then(m => ({ default: m.CalendarEventFormPage })));
const AcademicYearsPage = lazy(() => import("@/pages/calendar").then(m => ({ default: m.AcademicYearsPage })));
const AcademicTermsPage = lazy(() => import("@/pages/calendar").then(m => ({ default: m.AcademicTermsPage })));

// Curriculum
const CurriculumIndexPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.CurriculumIndexPage })));
const SubjectsPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.SubjectsPage })));
const SubjectDetailPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.SubjectDetailPage })));
const UnitDetailPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.UnitDetailPage })));
const GradeLevelsPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.GradeLevelsPage })));
const GradeLevelDetailPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.GradeLevelDetailPage })));
const CurriculumUnitsPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.CurriculumUnitsPage })));
const CurriculumLessonsPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.CurriculumLessonsPage })));
const LessonPlanTemplatesPage = lazy(() => import("@/pages/curriculum").then(m => ({ default: m.LessonPlanTemplatesPage })));

// Notifications
const NotificationQueuePage = lazy(() => import("@/pages/notifications").then(m => ({ default: m.NotificationQueuePage })));
const NotificationHistoryPage = lazy(() => import("@/pages/notifications").then(m => ({ default: m.NotificationHistoryPage })));
const SendNotificationPage = lazy(() => import("@/pages/notifications").then(m => ({ default: m.SendNotificationPage })));

// Timecard
const TimeClockPage = lazy(() => import("@/pages/timecard").then(m => ({ default: m.TimeClockPage })));
const AdminTimeRecordsPage = lazy(() => import("@/pages/timecard").then(m => ({ default: m.AdminTimeRecordsPage })));

const staffAndTeachers = ["super_user", "admin", "principal", "staff", "teacher"] as const;
const adminStaff = ["super_user", "admin", "principal", "staff"] as const;
const adminOnly = ["super_user", "admin", "principal"] as const;
// Curriculum browse pages are read-only; teachers + staff need to see them
// for lesson planning. Write paths are gated server-side via
// CURRICULUM_MANAGE_PERMISSIONS — UI route just controls discoverability.
const curriculumBrowse = [
  "super_user", "admin", "principal", "staff", "teacher",
] as const;

export const operationsRoutes: RouteObject[] = [
  // Attendance Routes
  {
    path: "/attendance",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><AttendanceDashboardPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/attendance/sessions",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><SessionsListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/attendance/sessions/:sessionId",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><TakeAttendancePage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/attendance/classes/:classId/schedules",
    element: (
      <ProtectedRoute roles={[...adminStaff]}>
        <AppShell>
          <LazyPage><ClassSchedulesPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Hifdh Progress v2
  {
    path: "/hifdh/v2",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><HifdhHomePage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/hifdh/v2/classes/:classId/day",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><HifdhClassDayPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/hifdh/v2/classes/:classId/months/:month",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><HifdhClassMonthPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/hifdh/v2/students/:studentId",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><HifdhStudentPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/hifdh/v2/students/:studentId/plan",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><HifdhPlanPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/hifdh/v2/students/:studentId/certificates/:certificateId",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><HifdhCertificatePage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/hifdh/v2/classes/:classId/certificates/print",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><HifdhClassCertificatesPrintPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Hifdh Routes. The old (v1) pages that wrote progress -- dashboard,
  // daily-progress form, memorization, monthly reports and targets -- lead
  // to Hifdh Progress (v2): what they saved, v2 never reads (bookmarks and
  // browser history still reach these URLs). The old report card stays,
  // read-only, for saved links (nothing in the app links to it now; it goes
  // with the rest of v1 in PR 6).
  {
    path: "/hifdh",
    element: <RedirectKeepingState to="/hifdh/v2" />,
  },
  {
    path: "/hifdh/classes/:classId/progress",
    element: <HifdhV1DayRedirect />,
  },
  {
    path: "/hifdh/students/:studentId/memorization",
    element: <HifdhV1StudentRedirect />,
  },
  {
    path: "/hifdh/classes/:classId/reports",
    element: <RedirectKeepingState to="/hifdh/v2" />,
  },
  {
    path: "/hifdh/report-card/:studentId/:month",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><HifdhReportCardPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/hifdh/targets/:classId/:month",
    element: <RedirectKeepingState to="/hifdh/v2" />,
  },

  // Calendar Routes
  {
    path: "/calendar",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><CalendarViewPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/calendar/events",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><CalendarEventsListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/calendar/events/new",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><CalendarEventFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/calendar/events/:id/edit",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><CalendarEventFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/calendar/academic-years",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><AcademicYearsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/calendar/terms",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><AcademicTermsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><CurriculumIndexPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum/subjects",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><SubjectsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum/subjects/:subjectId",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><SubjectDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum/units",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><CurriculumUnitsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum/units/:unitId",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><UnitDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum/lessons",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><CurriculumLessonsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum/lesson-plan-templates",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><LessonPlanTemplatesPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum/grade-levels",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><GradeLevelsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/curriculum/grade-levels/:id",
    element: (
      <ProtectedRoute roles={[...curriculumBrowse]}>
        <AppShell>
          <LazyPage><GradeLevelDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Notification Routes
  {
    path: "/notifications",
    element: <RedirectKeepingState to="/notifications/queue" />,
  },
  {
    path: "/notifications/queue",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "staff"]}>
        <AppShell>
          <LazyPage><NotificationQueuePage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/notifications/history",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "staff"]}>
        <AppShell>
          <LazyPage><NotificationHistoryPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/notifications/send",
    element: (
      <ProtectedRoute roles={["super_user", "admin"]}>
        <AppShell>
          <LazyPage><SendNotificationPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Timecard Routes
  {
    path: "/timecard",
    element: (
      <ProtectedRoute roles={[...staffAndTeachers]}>
        <AppShell>
          <LazyPage><TimeClockPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/timecard",
    element: (
      <ProtectedRoute roles={[...adminOnly]}>
        <AppShell>
          <LazyPage><AdminTimeRecordsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
];
